import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import { attemptsOptions, intervalTimeOptions } from '../../../utils/common';

import {
  getProcessWiseListsOptions,
  processAllOptionGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { dispositionListWiseGet } from '../../../store/slices/tenantSlices/settingSlice';
import {
  leadRetriesAdd,
  leadRetriesUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditLeadRetriesModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [listOptions, setListOptions] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [dispositionOptions, setDispositionOptions] = useState([]);

  const initialValues = {
    process_id: '',
    interval_type: 'enable',
    status: 'true',
    interval_time: 0,
    list_id: '',
    attempts: '',
    disposition: []
  };

  const validationSchema = Yup.object({
    process_id: Yup.string().required('process is required'),
    attempts: Yup.string().required('attempts is required'),
    list_id: Yup.string().required('list_id is required'),
    disposition: Yup.array().min(1, 'disposition is required').required('disposition is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        if (values.interval_type === 'disable') {
          delete payload['interval_time'];
        }
        const response = await dispatch(leadRetriesUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        if (values.interval_type === 'disable') {
          delete payload['interval_time'];
        }
        const response = await dispatch(leadRetriesAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getListOption = async () => {
    try {
      const response = await dispatch(
        getProcessWiseListsOptions({ processId: values?.process_id })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.list_name,
          value: val.id
        }));
        setListOptions(options);
      } else {
        setListOptions([]);
      }
    } catch (error) {
      setListOptions([]);
      console.log('error', error);
    }
  };

  const getDispositionOptions = async () => {
    try {
      const response = await dispatch(
        dispositionListWiseGet({ listId: values?.list_id })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.status,
          value: val.status
        }));
        setDispositionOptions(options);
      } else {
        setDispositionOptions([]);
      }
    } catch (error) {
      setDispositionOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values?.process_id) {
      getListOption();
    } else {
      setListOptions([]);
      setFieldValue('list_id', '');
    }
  }, [values?.process_id]);

  useEffect(() => {
    if (values?.list_id) {
      getDispositionOptions();
    } else {
      setDispositionOptions([]);
      setFieldValue('disposition', []);
    }
  }, [values?.list_id]);

  useEffect(() => {
    getProcessOption();
  }, []);

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...values,
        process_id: Number(editRecord?.process_id),
        interval_type: editRecord?.interval_type || 'enable',
        status: String(editRecord?.status) || 'true',
        interval_time: Number(editRecord?.interval_time) || 0,
        list_id: editRecord?.list_id,
        attempts: Number(editRecord?.attempts),
        disposition: editRecord?.disposition || []
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Lead Retries' : 'Add Lead Retries'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormSelect
                name="process_id"
                label="Process"
                placeholder="Select Process"
                value={values.process_id}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="interval_type"
                label="Interval Type"
                value={String(values.interval_type === 'enable')}
                onChange={() => {
                  setFieldValue(
                    'interval_type',
                    values.interval_type === 'enable' ? 'disable' : 'enable'
                  );
                  if (values.interval_type === 'enable') {
                    setFieldValue('interval_time', 0);
                  }
                }}
              />
            </Col>
            {values.interval_type === 'enable' ? (
              <Col lg="3">
                <FormSelect
                  name="interval_time"
                  label="Interval Time"
                  placeholder="Select Interval Time"
                  value={values.interval_time}
                  options={intervalTimeOptions()}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            ) : null}
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label="List"
                placeholder="Select List"
                value={values.list_id}
                onChange={handleChange}
                onBlur={handleBlur}
                name="list_id"
                options={listOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label="Attempts"
                placeholder="Select Attempts"
                value={values.attempts}
                onChange={handleChange}
                onBlur={handleBlur}
                name="attempts"
                options={attemptsOptions}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3" md="3" className="flex-row">
              <FormSelect
                label="Disposition"
                placeholder="Select Disposition"
                value={values.disposition}
                onChange={handleChange}
                onBlur={handleBlur}
                name="disposition"
                options={dispositionOptions}
                touched={touched}
                errors={errors}
                isMulti
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditLeadRetriesModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditLeadRetriesModal;
